/**
 * Main Layout - Olympic Fit Wellness
 * Premium design with proper OFW logo and contrast navigation
 */
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// OFW Logo Component - Proper W font (Outfit has clean W, not VV) with Magenta W
function OFWLogo({ light = false }) {
  return (
    <div className="flex items-center gap-4">
      {/* OFW Box - Uses Outfit font for proper W, W is magenta */}
      <div className={`h-12 px-5 flex items-center justify-center transition-colors duration-500 ${
        light ? 'bg-white' : 'bg-black'
      }`}>
        <span className={`text-2xl font-heading font-medium tracking-[0.08em] ${
          light ? 'text-black' : 'text-white'
        }`} style={{ fontFamily: 'Outfit, sans-serif' }}>OF</span>
        <span className="text-2xl font-heading font-medium tracking-[0.08em]" style={{ fontFamily: 'Outfit, sans-serif', color: '#DB2777' }}>W</span>
      </div>
      {/* Full name - Larger text */}
      <span className={`hidden xl:block text-sm font-heading font-medium tracking-[0.08em] leading-tight ${light ? 'text-white' : 'text-black'}`}>
        OLYMPIC FIT<br/>WELLNESS
      </span>
    </div>
  );
}

export function MainLayout({ children }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAuthenticated, signOut } = useAuth();

  // Determine if current page has dark hero
  const darkHeroPages = ['/', '/about', '/services', '/membership', '/locations', '/contact', '/classes', '/trainers', '/about/founders'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'ABOUT', path: '/about' },
    { name: 'SERVICES', path: '/services' },
    { name: 'MEMBERSHIP', path: '/membership' },
    { name: 'LOCATIONS', path: '/locations' },
    { name: 'TRAINERS', path: '/trainers' },
    { name: 'CONTACT', path: '/contact' },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Determine text colors based on scroll state and page - Luxury styling
  const navBg = isScrolled ? 'bg-white border-b border-neutral-200' : (hasDarkHero ? 'bg-transparent' : 'bg-white');
  const textColor = isScrolled ? 'text-neutral-900' : (hasDarkHero ? 'text-white' : 'text-neutral-900');
  const hoverColor = 'hover:opacity-60';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
        <nav className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <OFWLogo light={!isScrolled && hasDarkHero} />
            </Link>

            {/* Desktop Navigation - Larger readable text */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm font-heading font-medium tracking-[0.06em] transition-all duration-300 ${textColor} ${hoverColor} ${
                    location.pathname === link.path ? 'opacity-60' : ''
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Auth Buttons - Larger text */}
            <div className="hidden lg:flex items-center gap-2">
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center gap-2 transition-colors ${textColor}`}
                    data-testid="user-menu-button"
                  >
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="w-9 h-9" />
                    ) : (
                      <div className="w-9 h-9 bg-black flex items-center justify-center">
                        <span className="text-white font-heading text-sm">
                          {profile?.full_name?.[0] || 'U'}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-heading tracking-wide">{profile?.full_name?.split(' ')[0] || 'User'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-neutral-200 py-2 min-w-[200px] shadow-lg">
                      <Link
                        to="/portal"
                        className="block px-5 py-3 text-sm font-heading tracking-wide text-neutral-700 hover:bg-neutral-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        DASHBOARD
                      </Link>
                      <Link
                        to="/portal/settings"
                        className="block px-5 py-3 text-sm font-heading tracking-wide text-neutral-700 hover:bg-neutral-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        SETTINGS
                      </Link>
                      {['admin', 'founder', 'manager'].includes(profile?.role) && (
                        <Link
                          to="/admin"
                          className="block px-5 py-3 text-sm font-heading tracking-wide text-neutral-700 hover:bg-neutral-50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          ADMIN PANEL
                        </Link>
                      )}
                      <hr className="my-2 border-neutral-100" />
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full text-left px-5 py-3 text-sm font-heading tracking-wide text-red-600 hover:bg-red-50 flex items-center gap-2"
                        data-testid="logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        SIGN OUT
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`px-6 py-3 text-sm font-heading font-medium tracking-[0.06em] border transition-all duration-300 ${
                      isScrolled 
                        ? 'border-black text-black hover:bg-black hover:text-white' 
                        : hasDarkHero 
                          ? 'border-white text-white hover:bg-white hover:text-black'
                          : 'border-black text-black hover:bg-black hover:text-white'
                    }`}
                    data-testid="login-button"
                  >
                    LOGIN
                  </Link>
                  <Link
                    to="/membership"
                    className={`px-6 py-3 text-sm font-heading font-medium tracking-[0.06em] transition-all duration-300 ${
                      isScrolled
                        ? 'bg-black text-white hover:bg-neutral-800'
                        : hasDarkHero
                          ? 'bg-white text-black hover:bg-neutral-100'
                          : 'bg-black text-white hover:bg-neutral-800'
                    }`}
                    data-testid="join-button"
                  >
                    JOIN
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-neutral-100 py-6 -mx-4 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block py-4 text-base font-heading font-medium tracking-[0.06em] ${
                    location.pathname === link.path ? 'opacity-60' : 'text-neutral-900'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="mt-6 pt-6 border-t border-neutral-100 space-y-3">
                {isAuthenticated ? (
                  <>
                    <Link to="/portal" className="block py-3 text-base font-heading text-neutral-900">
                      Dashboard
                    </Link>
                    <button onClick={handleLogout} className="text-base text-red-600">
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block w-full border border-neutral-300 text-neutral-900 py-4 text-center font-heading font-medium text-base tracking-wide">
                      LOGIN
                    </Link>
                    <Link to="/membership" className="block w-full bg-black text-white py-4 text-center font-heading font-medium text-base tracking-wide">
                      JOIN
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-neutral-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <OFWLogo light={true} />
            </Link>
            <p className="text-neutral-400 mb-6 text-sm max-w-xs">
              A comprehensive wellness clinic where fitness, nutrition, recovery, and community come together to transform lives.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-[#3B82F6] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-[#3B82F6] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-[#3B82F6] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-[#3B82F6] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>

          {/* About */}
          <div>
            <h4 className="font-heading font-semibold text-sm tracking-wider mb-6">ABOUT</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-neutral-400 hover:text-white text-sm transition-colors">Our Story</Link></li>
              <li><Link to="/about/founders" className="text-neutral-400 hover:text-white text-sm transition-colors">Meet the Founders</Link></li>
              <li><Link to="/careers" className="text-neutral-400 hover:text-white text-sm transition-colors">Careers</Link></li>
              <li><Link to="/press" className="text-neutral-400 hover:text-white text-sm transition-colors">Press</Link></li>
            </ul>
          </div>

          {/* Membership */}
          <div>
            <h4 className="font-heading font-semibold text-sm tracking-wider mb-6">MEMBERSHIP</h4>
            <ul className="space-y-3">
              <li><Link to="/membership" className="text-neutral-400 hover:text-white text-sm transition-colors">Plans & Pricing</Link></li>
              <li><Link to="/locations" className="text-neutral-400 hover:text-white text-sm transition-colors">Find a Location</Link></li>
              <li><Link to="/classes" className="text-neutral-400 hover:text-white text-sm transition-colors">Class Schedule</Link></li>
              <li><Link to="/login" className="text-neutral-400 hover:text-white text-sm transition-colors">Member Login</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading font-semibold text-sm tracking-wider mb-6">CONTACT</h4>
            <ul className="space-y-3">
              <li><a href="tel:+14695851023" className="text-neutral-400 hover:text-white text-sm transition-colors">(469) 585-1023</a></li>
              <li><a href="mailto:info@olympicfitwellness.com" className="text-neutral-400 hover:text-white text-sm transition-colors">info@olympicfitwellness.com</a></li>
              <li className="text-neutral-400 text-sm">2601 E State Hwy 121<br/>Building 1 Suite 114<br/>Lewisville, TX 75056</li>
            </ul>
            <div className="mt-4 text-xs text-neutral-500">
              <p>Mon-Thu: 6AM-8PM</p>
              <p>Fri: 6AM-3PM • Sat: 8AM-3PM</p>
              <p>Sun: 1-4PM</p>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} Olympic Fit Wellness. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-neutral-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/accessibility" className="hover:text-white transition-colors">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default MainLayout;
